/**
 * The Dashboard "quick action" catalogue - shortcut buttons for common
 * create/navigate actions (New Quotation, Add Task, Import Products, ...).
 * A plain TS constant (not DB-seeded), same reasoning as
 * `PLAN_FEATURE_CATALOG`: this is UI metadata the backend never needs to
 * query, just validate incoming keys against when a user customizes their
 * own set. Each entry's `requiredPermission`/`requiredMenuKey` mirror the
 * exact guard a click-through would hit - `apps/web/src/auth/AuthContext.tsx`'s
 * `hasPermission`/`hasMenuAccess` are the single source of truth for
 * whether an action is currently usable, this catalogue just says which
 * checks apply to which action.
 *
 * `href` is omitted for an action that opens a dedicated Dashboard modal
 * instead of navigating (currently only `add_followup`, since creating a
 * follow-up has no standalone page - it's always attached to a contact,
 * so the Dashboard provides its own "pick a contact, then add" flow).
 */
export const QUICK_ACTION_CATALOG = [
    {
        key: 'new_quotation',
        label: 'New Quotation',
        group: 'Quotations',
        requiredPermission: 'products.read',
        requiredMenuKey: 'quotations',
        href: '/quotations?quickAction=new',
    },
    {
        key: 'add_task',
        label: 'Add Task',
        group: 'Tasks & Follow-ups',
        requiredPermission: 'tasks.manage',
        requiredMenuKey: 'tasks',
        href: '/tasks',
    },
    {
        key: 'add_followup',
        label: 'Add Follow-up',
        group: 'Tasks & Follow-ups',
        requiredPermission: 'tasks.manage',
        requiredMenuKey: 'follow_ups',
    },
    {
        key: 'new_contact',
        label: 'New Contact',
        group: 'Contacts & Leads',
        requiredPermission: 'contacts.create',
        requiredMenuKey: 'contacts',
        href: '/contacts/new',
    },
    {
        key: 'import_contacts',
        label: 'Import Contacts',
        group: 'Contacts & Leads',
        requiredPermission: 'contacts.create',
        requiredMenuKey: 'contacts',
        href: '/contacts/import',
    },
    {
        key: 'new_lead',
        label: 'New Lead',
        group: 'Contacts & Leads',
        requiredPermission: 'leads.create',
        requiredMenuKey: 'leads',
        href: '/leads/new',
    },
    {
        key: 'new_product',
        label: 'New Product/Service',
        group: 'Products & Services',
        requiredPermission: 'products.manage',
        requiredMenuKey: 'products_services',
        href: '/products-services/new',
    },
    {
        key: 'import_products',
        label: 'Import Products (CSV/Excel)',
        group: 'Products & Services',
        requiredPermission: 'products.manage',
        requiredMenuKey: 'products_services',
        href: '/products-services?quickAction=import',
    },
    {
        key: 'scan_catalog',
        label: 'Scan a Catalog Photo/PDF',
        group: 'Products & Services',
        requiredPermission: 'products.manage',
        requiredMenuKey: 'products_services',
        href: '/products-services?quickAction=scan',
    },
    {
        key: 'new_automation',
        label: 'New Automation',
        group: 'Automation & Marketing',
        requiredPermission: 'automation.create',
        requiredMenuKey: 'automations',
        href: '/automations/new',
    },
    {
        key: 'new_campaign',
        label: 'New Campaign',
        group: 'Automation & Marketing',
        requiredPermission: 'campaigns.manage',
        requiredMenuKey: 'campaigns',
        href: '/campaigns/new',
    },
    {
        key: 'new_pipeline',
        label: 'New Pipeline',
        group: 'Contacts & Leads',
        requiredPermission: 'pipelines.manage',
        requiredMenuKey: 'pipelines',
        href: '/pipelines/new',
    },
];
/** Shown when a user has never customized their Dashboard (`dashboardQuickActions === null`), filtered by what they currently have access to. */
export const DEFAULT_QUICK_ACTION_KEYS = [
    'new_quotation',
    'add_task',
    'add_followup',
    'new_contact',
    'new_lead',
    'import_products',
];
export const QUICK_ACTION_KEYS = QUICK_ACTION_CATALOG.map((a) => a.key);
//# sourceMappingURL=quick-actions.js.map